import "twin.macro";

import { useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import { Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import AddSetupFeeModal from "@components/Purchasing/AddSetupFeeModal";
import PurchaseOrderControl from "@components/Purchasing/PurchaseOrderControl";
import PurchaseOrderDetail from "@components/Purchasing/PurchaseOrderDetail";
import PurchaseOrderOptions from "@components/Purchasing/PurchaseOrderOptions";
import PurchaseOrderVariantOverviewTable from "@components/Purchasing/PurchaseOrderVariantOverviewTable";
import PurchaseOrderVariantTable from "@components/Purchasing/PurchaseOrderVariantTable";
import PurchasingAgentWarehouseAddress from "@components/Purchasing/PurchasingAgentWarehouseAddress";
import ShippingParameterTable from "@components/Purchasing/ShippingParameterTable";
import {
  Contained,
  OpaqueCard,
  StyledButton,
} from "@components/StyledComponents";
import OptionsMenu from "@components/Utility/OptionsMenu";
import OrderPatchLoading from "@components/Utility/OrderPatchLoading";
import BeaconRangesTable from "@features/beacons/components/BeaconRangesTable";
import { usePurchaseOrder } from "@features/purchaseOrders/context/PurchaseOrderContext";
import PurchaseOrderProvider from "@features/purchaseOrders/context/PurchaseOrderProvider";
import WarehouseLabelDownloadButton from "@features/purchaseOrders/packoutLabelPdf/WarehouseLabelDownloadButton";
import { Title, ToggleButtons } from "@features/ui";
import DocTitle from "@utility/DocTitle";
import permissions from "@utils/permissions";
import useRoleIs from "@utils/useRoleIs";

/*
The purchase order view is used for viewing and editing of a single purchase order. Purchase orders
are visible to admin users, purchasers, and suppliers.  Suppliers have limited capabilities when
editing purchase orders, and only have access to a couple fields.
*/

const useStyles = makeStyles((theme: any) => ({
  ...theme.global,
}));

const PurchaseOrderContent = () => {
  const { poId } = useParams();

  const classes: any = useStyles();

  const roleIs = useRoleIs();

  const [setupFeeOpen, setSetupFeeOpen] = useState(false);
  const [tableView, setTableView] = useState(
    roleIs(permissions.supplier) ? "overview" : "itemized"
  );

  const { purchaseOrder } = usePurchaseOrder();
  const hasBeacons = useSelector(
    (state: any) => state.currentUser.organization.hasBeacons
  );

  // Determine the address view based on the purchase order type and user role
  const isSupplierShippingToWarehouse =
    purchaseOrder.requiresPurchasingAgent &&
    roleIs(permissions.supplier) &&
    purchaseOrder.type !== "on-demand";

  const addressView =
    isSupplierShippingToWarehouse || purchaseOrder.type === "inventory"
      ? !roleIs("purchasing-agent")
        ? "warehouse"
        : null
      : "shipping";

  const handleSetupFeeOpen = () => setSetupFeeOpen(true);
  const handleSetupFeeClose = () => setSetupFeeOpen(false);

  return (
    <>
      {setupFeeOpen && (
        <AddSetupFeeModal
          open
          handleClose={handleSetupFeeClose}
          purchaseOrderId={purchaseOrder.id}
        />
      )}
      <DocTitle title={`Purchase Order #${poId}`} />
      <Contained>
        <header tw="flex justify-between items-center">
          <Title backUrl={`/purchasing/purchase-order-history`}>
            Purchase Order #{poId}
          </Title>

          <div tw="flex gap-2">
            <WarehouseLabelDownloadButton />
            <OptionsMenu
              skipCloseOnClick
              title="Options Menu"
              component={(props) => (
                <StyledButton
                  tw="bg-white"
                  outlined
                  endIcon={<MoreVertRoundedIcon />}
                  {...props}
                >
                  Options
                </StyledButton>
              )}
            >
              <PurchaseOrderOptions />
            </OptionsMenu>
          </div>
        </header>
        <br />
        <div tw="mb-10">
          <PurchaseOrderDetail />
          <br />
          <div tw="flex justify-between items-center">
            <Typography className={classes.titleText}>
              Itemized Costs
            </Typography>
            <ToggleButtons
              options={[
                { title: "Overview", value: "overview" },
                { title: "Itemized", value: "itemized" },
              ]}
              onChange={setTableView}
              value={tableView}
            />
          </div>
          <OpaqueCard tw="mt-2 p-0">
            {tableView === "itemized" ? (
              <PurchaseOrderVariantTable
                handleSetupFeeOpen={handleSetupFeeOpen}
              />
            ) : (
              <PurchaseOrderVariantOverviewTable setTableView={setTableView} />
            )}
          </OpaqueCard>
          <PurchaseOrderControl />
          <br />
          {addressView === "warehouse" && (
            <>
              <Typography className={classes.titleText}>
                Warehouse Detail
              </Typography>
              <OpaqueCard tw="mt-2 p-0">
                <PurchasingAgentWarehouseAddress
                  warehouse={purchaseOrder.warehouse}
                />
              </OpaqueCard>
            </>
          )}
          {addressView === "shipping" && (
            <>
              <Typography className={classes.titleText}>
                Shipping Detail
              </Typography>
              <OpaqueCard tw="mt-2 p-0">
                <ShippingParameterTable />
              </OpaqueCard>
            </>
          )}
          <br />
        </div>
        <br />
        {roleIs(permissions.beaconRanges) && hasBeacons && (
          <div>
            <Typography className={classes.titleText}>
              Beacon UID Ranges:
            </Typography>
            <BeaconRangesTable tw="mt-2" />
          </div>
        )}
        <br />
        <br />
        <OrderPatchLoading />
      </Contained>
    </>
  );
};

const PurchaseOrder = () => {
  const { poId } = useParams();
  return (
    <PurchaseOrderProvider purchaseOrderId={poId!}>
      <PurchaseOrderContent />
    </PurchaseOrderProvider>
  );
};
export default PurchaseOrder;
